import { Box, Button, Container, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { styled } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { useAddCityDataMutation, useUpdateCityDataMutation, useUploadCityImageMutation } from '../store/api/citiesApi';
import Loading from './Loading';
import LocationSearchInput from '../sections/@dashboard/parkingspaces/PlaceLocationAutoComplete';
import * as Yup from 'yup';

type Props = {};

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const citySchema = Yup.object().shape({
  countryName: Yup.string().required('This field is required'),
  stateName: Yup.string().required('This field is required'),
  cityName: Yup.string().required('This field is required'),
});

const AddOrUpdateCity = (props: Props) => {
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    countryName: '',
    stateName: '',
    cityName: '',
  });
  const [isImgUrl, setIsImgUrl] = useState(false);
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const [addCityData, addCityDataResponse] = useAddCityDataMutation();
  const [updateCityData, updateCityDataResponse] = useUpdateCityDataMutation();
  const [uploadCityImage, uploadCityImageResponse] = useUploadCityImageMutation();
  const [submitData, setSubmitData] = useState<any | null>();
  const [address, setAddress] = useState<any | null>();
  // city image ref
  const cityImageInput: any = useRef();
  // city image state
  const [cityImage, setCityImage] = useState<null | any>();

  const handleAddOrUpdateCity = (values: any, action: any) => {
    if (!state.editMode) {
      setSubmitData({
        cityName: values?.cityName,
        countryName: values?.countryName,
        imagePath: cityImage,
        stateName: values?.stateName,
      });
      const formData = new FormData();
      formData.append('file', cityImage);
      uploadCityImage({
        token: token,
        payload: formData,
      });
    } else {
      setSubmitData({
        id: state.editData.id,
        cityName: address !== undefined ? values?.cityName : state.editData.name,
        countryName: address !== undefined ? values?.countryName : state.editData.countryName,
        stateName: address !== undefined ? values?.stateName : state.editData.stateName,
      });
      if (!isImgUrl) {
        const formData = new FormData();
        formData.append('file', cityImage);
        uploadCityImage({
          token: token,
          payload: formData,
        });
      } else {
        updateCityData({
          token: token,
          payload: {
            id: state.editData.id,
            cityName: address !== undefined ? address?.city : state.editData.name,
            countryName: address !== undefined ? address?.country : state.editData.countryName,
            imagePath: cityImage.split('?')[0].split('com/')[1],
            stateName: address !== undefined ? address?.state : state.editData.stateName,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (state.editData) {
      setInitialValues({
        countryName: state.editData.countryName,
        stateName: state.editData.stateName,
        cityName: state.editData.name,
      });
      setCityImage(state.editData.imageUrl);
      setIsImgUrl(true);
    }
  }, []);

  useEffect(() => {
    if (uploadCityImageResponse.isSuccess && !state.editMode) {
      addCityData({
        token: token,
        payload: { ...submitData, imagePath: uploadCityImageResponse.data.imagePath },
      });
    } else if (uploadCityImageResponse.isSuccess && !isImgUrl) {
      updateCityData({
        token: token,
        payload: { ...submitData, imagePath: uploadCityImageResponse.data.imagePath },
      });
    } else if (uploadCityImageResponse.isError) {
      toast.error('Image size is too large');
    }
  }, [uploadCityImageResponse.isSuccess, uploadCityImageResponse.isError]);

  useEffect(() => {
    if (addCityDataResponse.isSuccess) {
      toast.success('City added successfully');
      navigate('/dashboard/cities', { replace: true });
    } else if (addCityDataResponse.isError) {
      let errorData: any = addCityDataResponse.error;
      let errorCode: any = errorData?.data?.serviceErrors[0]?.errorCode;
      toast.error(errorCode === 'CITY_EXIST_EXCEPTION_MSG' ? 'City already exists' : '');
    }
    if (updateCityDataResponse.isSuccess) {
      toast.success('City updated successfully');
      navigate('/dashboard/cities', { replace: true });
    } else if (updateCityDataResponse.isError) {
      let errorData: any = updateCityDataResponse?.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [
    addCityDataResponse.isSuccess,
    addCityDataResponse.isError,
    updateCityDataResponse.isSuccess,
    updateCityDataResponse.isError,
  ]);

  const locationChange = (item: any) => {
    setInitialValues({
      countryName: item.country,
      stateName: item.state,
      cityName: item.city,
    });
    setAddress(item);
  };

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit City' : 'Add City'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <Box>
            <input
              type="file"
              name="city-image"
              id="city-image"
              ref={cityImageInput}
              accept="image/gif, image/jpeg, image/png"
              onChange={(event: any) => {
                if (state.editMode && isImgUrl) {
                  setIsImgUrl(false);
                }
                setCityImage(event.target.files[0]);
              }}
              hidden
            />
            <Box
              onClick={() => {
                cityImageInput.current.click();
              }}
            >
              {cityImage && (
                <Box style={{ position: 'relative' }}>
                  <img
                    src={state.editMode && isImgUrl ? cityImage : URL.createObjectURL(cityImage)}
                    alt=""
                    height={200}
                    width={200}
                    style={{ borderRadius: '100px' }}
                  />
                  <Box
                    style={{
                      background: 'white ',
                      padding: '3px',
                      borderRadius: '100px',
                      position: 'absolute',
                      bottom: '4px',
                      right: '40px',
                      fontSize: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '40px',
                      width: '40px',
                    }}
                  >
                    <AddPhotoAlternateIcon />
                  </Box>
                </Box>
              )}
              {!cityImage && (
                <Box
                  style={{
                    background: 'lightgray',
                    height: 200,
                    width: 200,
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <h5>Upload photo</h5>
                </Box>
              )}
            </Box>
            <Box style={{ textAlign: 'center', marginTop: '10px' }}>
              <p>Allowed *.jpeg, *.jpg, *.png, *.gif</p>
              <p> Max size of 4.0 MB</p>
            </Box>
          </Box>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddOrUpdateCity}
              validationSchema={citySchema}
            >
              {({ handleBlur, handleChange, handleSubmit, values, touched, errors }) => (
                <Box style={{ width: '100%' }}>
                  <Box>
                    <Typography variant="h5" marginTop={2} gutterBottom component="div">
                      Search Place
                    </Typography>
                    <LocationSearchInput
                      locationDetail={(item: any) => locationChange(item)}
                      value={''}
                    ></LocationSearchInput>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Country name */}
                    <Box style={{ width: '100%', marginRight: '20px' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Country Name
                      </Typography>
                      <TextField
                        fullWidth
                        autoComplete="countryName"
                        type="text"
                        name="countryName"
                        label="Please enter country name"
                        value={values.countryName}
                        disabled={true}
                        error={Boolean(touched.countryName && errors.countryName)}
                        helperText={touched.countryName && errors.countryName}
                      />
                    </Box>
                    {/* State name */}
                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        State Name
                      </Typography>
                      <TextField
                        fullWidth
                        autoComplete="stateName"
                        type="text"
                        name="stateName"
                        label="Please enter state name"
                        value={values.stateName}
                        disabled={true}
                        error={Boolean(touched.stateName && errors.stateName)}
                        helperText={touched.stateName && errors.stateName}
                      />
                    </Box>
                  </Box>
                  {/* City name */}
                  <Box style={{ width: '100%' }}>
                    <Typography variant="h5" marginTop={2} gutterBottom component="div">
                      City Name
                    </Typography>
                    <TextField
                      fullWidth
                      autoComplete="cityName"
                      type="text"
                      name="cityName"
                      label="Please enter city name"
                      value={values.cityName}
                      disabled={true}
                      error={Boolean(touched.cityName && errors.cityName)}
                      helperText={touched.cityName && errors.cityName}
                    />
                  </Box>
                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      onClick={() => {
                        navigate('/dashboard/cities');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      component="span"
                      onClick={() => {
                        if (!cityImage) {
                          toast.error('Please upload an image');
                        } else {
                          handleSubmit();
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {addCityDataResponse.isLoading || updateCityDataResponse.isLoading || uploadCityImageResponse.isLoading ? (
        <Loading />
      ) : null}
    </Box>
  );
};

export default AddOrUpdateCity;
